<template>
  <article>
    <activity-stage>
      <template slot="instructions">
        <slot name="instructions"></slot>
      </template>
      <section slot="content" class="whack-a-mole" :style="{ backgroundImage: `url(${sky})` }">
        <h2 class="word" role="heading" v-if="currentRound?.answer">
          <blinking-cursor-text
            :text-step="0"
            :shouldBeCurrentStep="shouldTypeWord && !hasTypedWord"
            :shouldBeCompletedStep="hasTypedWord"
            :text="currentRound?.answer"
          ></blinking-cursor-text>
        </h2>
        <div id="sentence" class="sentence">
          <blinking-cursor-text
            :text-step="0"
            :shouldBeCurrentStep="shouldTypeSentence && !hasTypedSentence"
            :shouldBeCompletedStep="hasTypedSentence"
            :text="currentRound.sentence ?? ''"
          ></blinking-cursor-text>
        </div>
        <div class="moles-stage">
          <text-display-list class="syllables-wrapper" :text-size="'large'">
            <li
              v-for="(syllable, index) in currentRound.syllables"
              :key="index"
              class="syllable-container"
              :class="`syllable-${index} syllable-container-${currentRound.syllables.length}`"
            >
              <div :class="`dynamic-mole dynamic-mole-${index}`">
                <div class="syllable-banner">
                  <blinking-cursor-text
                    class="type-line"
                    :shouldBeCurrentStep="
                      moleIndex === index && !shouldTypeWord && !shouldTypeSentence
                    "
                    :shouldBeCompletedStep="
                      (isLastStepInRound && index < moleIndex) || shouldTypeWord
                    "
                    :text-step="progress.stepIndex"
                    :text="syllable"
                  >
                  </blinking-cursor-text>
                </div>
                <img
                  alt="Mole image"
                  class="mole-image"
                  :src="index % 2 == 0 ? leftMole : rightMole"
                />
              </div>
              <img
                v-if="index < currentRound.syllables.length"
                alt="Nest image"
                class="nest-image"
                :id="`nest-image-${index}`"
                :src="nest"
              />
            </li>
          </text-display-list>
          <img alt="Grass image" class="grass-image" :src="grass" />
        </div>
      </section>
    </activity-stage>
    <slot name="keyboard"></slot>
  </article>
</template>

<script>
import gsap, { TweenMax, TimelineLite } from 'gsap';

import ActivityManager from 'mixins/ActivityManager';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ErrorLogger from 'mixins/ErrorLogger';

import ActivityStage from 'components/activity/ActivityStage.vue';
import BlinkingCursorText from 'components/text-displays/BlinkingCursorText.vue';
import TextDisplayList from 'components/text-displays/TextDisplayList.vue';

import { activityTyping, application, mapMutations, mapGetters } from '../store/helpers';

import grass from 'assets/images/activities/whack-a-mole/grass.png';
import nest from 'assets/images/activities/whack-a-mole/nest.png';
import leftMole from 'assets/images/activities/whack-a-mole/left-mole.png';
import rightMole from 'assets/images/activities/whack-a-mole/right-mole.png';
import sky from 'assets/images/activities/whack-a-mole/sky.jpg';

export default {
  mixins: [ActivityManager, TypingActivityHelper, ErrorLogger],
  components: {
    BlinkingCursorText,
    TextDisplayList,
    ActivityStage
  },
  data() {
    return {
      grass,
      nest,
      leftMole,
      rightMole,
      sky,
      moleIndex: 0,
      shouldTypeWord: false,
      hasTypedWord: false,
      shouldTypeSentence: false,
      hasTypedSentence: false,
      externalKeyboardIntervalId: null
    };
  },
  mounted() {
    if (this.usingExternalKeyboard) {
      this.externalKeyboardIntervalId = setInterval(() => {
        if (this.isKeyboardMounted()) {
          this.setActivityContainerHeight();
          clearInterval(this.externalKeyboardIntervalId);
        }
      }, 500);
    }
    window.addEventListener('resize', this.setContainerHeight);
  },

  destroyed() {
    window.removeEventListener('resize', this.setContainerHeight);
  },

  beforeDestroy() {
    TweenMax.killAll();
  },

  computed: {
    ...mapGetters(application, ['usingExternalKeyboard']),
    typingText() {
      return this.currentRound.syllables[this.moleIndex];
    }
  },
  methods: {
    ...mapMutations(activityTyping, ['setNewTypingText', 'overwriteProgressText']),

    isKeyboardMounted() {
      const keyooardWrapperEl = document.getElementById('phoenix-keyboard-wrapper');
      return keyooardWrapperEl && keyooardWrapperEl.style.display !== 'none';
    },

    async loadActivity() {
      this.createRounds({ stepIndicator: 'syllableGroups' });
      const [sounds] = await this.loadAssets(this.loadSounds);
      this.sfx = sounds;
    },

    async loadSounds() {
      const soundPaths = this.rounds.map(round => round.answerAudio?.audio?.src).filter(Boolean);

      this.queueAudio(soundPaths);

      const assetCache = await this.downloadAudio();

      return {
        soundsPerRound: soundPaths.map(path => assetCache.get(path))
      };
    },

    stepCallback() {
      return this.currentRound.syllables[this.moleIndex];
    },

    startActivity() {
      const timeline = new TimelineLite();

      this.setupNewRound({ stepCallback: this.stepCallback });
      this.errorLoggerStart();

      timeline
        .to('.moles-stage,.sky-image,.nest-image,.grass-image,.dynamic-mole', 0.5, { autoAlpha: 1 })
        .call(this.checkMole)
        .call(this.enableKeyboard)
        .call(this.showKeyboard);

      this.$nextTick(this.setActivityContainerHeight);
    },

    handleCorrectKey(attempt) {
      this.sndType();
      this.incrementTypingIndex();

      this.captureKeyboardAttemptSegment(attempt, this.isEndOfCurrentSentence);

      if (this.isEndOfCurrentText) {
        this.finishWordPart();
        return;
      }
    },

    finishWordPart() {
      this.sndBell();
      const timeline = new TimelineLite();

      if (this.shouldTypeSentence) {
        this.hasTypedSentence = true;
        this.handleRoundEnd();
        return;
      }

      if (this.shouldTypeWord) {
        this.hasTypedWord = true;

        // Play the answer audio for the round
        this.sfx.soundsPerRound[this.progress.roundIndex]?.play();

        if (this.currentRound.sentence && !this.hasTypedSentence) {
          this.shouldTypeWord = false;
          this.shouldTypeSentence = true;
          this.overwriteProgressText({ currentText: this.currentRound.sentence });
          timeline
            .call(this.hideAllMoles)
            .delay(1)
            .call(this.setSentenceStepStyles)
            .delay(0.5)
            .to('.sentence', 0.5, { autoAlpha: 1 }, 1.5);

          return;
        }
        this.handleRoundEnd();
        return;
      }

      if (!this.isLastStepInRound) this.showMole(false);

      if (this.moleIndex < this.currentRound.syllables.length - 1) {
        timeline.delay(1).call(this.checkMole);
        timeline.delay(1.5).call(() => (this.moleIndex = this.moleIndex + 1));
      } else {
        this.disableKeyboard();
        timeline.delay(0.5).call(this.finishStep);
      }
    },

    finishStep() {
      const timeline = new TimelineLite();

      if (this.isEndOfRound) {
        this.handleRoundEnd();
        return;
      }

      this.moleIndex = 0;
      this.completeStep();
      this.setCurrentStep({ stepCallback: this.stepCallback });
      timeline.delay(1).call(this.checkMole);
    },

    resetRound() {
      this.moleIndex = 0;
      this.shouldTypeWord = false;
      this.shouldTypeSentence = false;
      this.hasTypedSentence = false;
      this.hasTypedWord = false;
      this.setSentenceStepStyles(true);
    },

    handleRoundEnd() {
      const timeline = new TimelineLite();
      if (!this.hasTypedWord) {
        this.enableKeyboard();
        this.shouldTypeWord = true;
        this.overwriteProgressText({ currentText: this.currentRound?.answer });
        return;
      }

      if (this.isEndOfActivity || this.isLastRound) {
        timeline
          .delay(1)
          .to(
            '.moles-stage,.sky-image,.nest-image,.grass-image,.dynamic-mole,.sentence,.word',
            0.5,
            { opacity: 0 }
          )
          .call(this.completeActivity);
      } else {
        timeline
          .call(() => (!this.hasTypedSentence ? this.hideAllMoles() : null))
          .to(
            '.moles-stage,.sky-image,.nest-image,.grass-image,.dynamic-mole,.sentence,.word',
            0.5,
            { opacity: 0 }
          )
          .call(() => this.setupNewRound({ stepCallback: this.stepCallback }))
          .call(this.resetRound)
          .delay(1.5)
          .to('.moles-stage,.sky-image,.nest-image,.grass-image,.dynamic-mole,.word', 0.5, {
            opacity: 1
          })
          .call(this.checkMole);
      }
    },

    setContainerHeight() {
      const el = document.getElementsByClassName('dynamic-mole')?.[0];
      const molesStage = document.getElementsByClassName(`moles-stage`)?.[0];

      gsap.to(molesStage, 0, {
        minHeight: `${el?.getBoundingClientRect()?.height}px`
      });
    },

    setSentenceStepStyles(isReset = false) {
      const scrollContainerEl = document.getElementById('scroll-container');
      const sentenceEl = document.getElementById('sentence');
      const molesStage = document.getElementsByClassName(`moles-stage`)?.[0];

      if (isReset) {
        sentenceEl.style['height'] = '0px';
        sentenceEl.style['width'] = '0px';
        return;
      }

      if (scrollContainerEl && sentenceEl) {
        molesStage.style['min-height'] = 'unset';
        sentenceEl.style['max-width'] = `${scrollContainerEl?.clientWidth}px`;
        sentenceEl.style['height'] = 'max-content';
        sentenceEl.style['width'] = '80%';
      }
    },

    showMole(show) {
      const el = document.getElementsByClassName(`dynamic-mole-${this.moleIndex}`)?.[0];

      this.setContainerHeight();

      if (show) {
        this.enableKeyboard();
        gsap.fromTo(el, 1, { bottom: -600 }, { bottom: 0 });
      } else {
        this.disableKeyboard();
        gsap.fromTo(el, 1, { bottom: 0 }, { bottom: -600 });
      }
    },

    hideAllMoles() {
      gsap.fromTo('.dynamic-mole', 1, { bottom: 0 }, { bottom: -600 });
    },

    checkMole() {
      this.$nextTick(() => {
        this.overwriteProgressText({ currentText: this.typingText });
        this.showMole(true);
      });
    },

    getKeyboardConfig() {
      return {
        showKeyboardAtStart: false
      };
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../assets/sass/_vars';

.tablet {
  .syllable-container,
  .dynamic-mole,
  .moles-stage {
    min-height: 15vh !important;
    height: 100%;
  }

  .dynamic-mole {
    transform: scale(0.8);
  }

  .mole-image {
    height: 60%;
  }
}

.whack-a-mole {
  height: 100%;
  width: 100%;
  min-height: max-content;
  min-width: max-content;
  background-size: cover;
  display: flex;
  flex-direction: column;
  overflow-x: auto;

  ul,
  li {
    margin: 0;
    padding: 0;
    list-style-type: none;
    height: 100%;
    width: 100%;
  }

  .word {
    margin: 30px auto;
    font-size: 40px;
    letter-spacing: 2px;
    display: flex;
    justify-content: center;
  }

  .sentence {
    visibility: hidden;
    height: 0px;
    width: 0px;
    font-size: 30px;
    letter-spacing: 2px;
    margin: auto;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .moles-stage {
    visibility: hidden;
    position: relative;
    height: calc(100% - 120px);
    min-height: 200px;
    width: 100%;
    min-width: max-content;
    display: flex;
    flex-direction: column;
    overflow-y: hidden;

    .grass-image {
      width: 100%;
      position: absolute;
      bottom: 0px;
      z-index: 3;
      min-height: 50px;
    }

    .syllables-wrapper {
      text-align: center;
      display: flex;
      height: 100%;
      width: 100%;

      .syllable-container {
        text-align: center;
        min-width: 200px;
        min-height: 292px;

        position: relative;
        height: 100%;
        width: 100%;
        display: flex;
        justify-content: center;
        position: relative;

        .dynamic-mole {
          position: absolute;
          bottom: -600px;
          max-height: 100%;
          max-height: 100%;
          min-height: 292px;
          height: max-content;
          width: 100%;
          display: block;

          .syllable-banner {
            background-color: $kwtWhite;
            border: 1px solid $kwtBlack;
            border-radius: 5px;
            height: max-content;
            padding: 0 20px;
            min-width: 100px;
            width: max-content;
            text-align: center;
            margin: auto;

            .type-line {
              margin: 15px auto;
            }
          }

          .mole-image {
            margin: -15px auto;
          }
        }

        .nest-image {
          width: 80%;
          min-width: 200px;
          max-width: 400px;
          height: 50px;
          z-index: 2;
          bottom: 0;
          position: absolute;
        }
      }
    }
  }
}
</style>
